/**
 * MAX Bridge wrapper.
 *
 * Две реализации:
 *  - real — использует window.WebApp, инжектируемый платформой MAX внутрь Mini App.
 *  - mock — для локальной разработки в браузере, имитирует initData/requestContact.
 *
 * Режим переключается переменной окружения VITE_MAX_MODE ('real' | 'mock').
 * По умолчанию в .env.development стоит mock.
 */

import type {
  MaxColorScheme,
  MaxInitData,
  MaxPlatform,
  MaxRequestContactResult,
  MaxWebApp,
} from '@/types/max';

const TAG = '[MAX Bridge]';

function logCall(method: string, args?: unknown): void {
  if (args === undefined) {
    console.log(`${TAG} ${method}()`);
  } else {
    console.log(`${TAG} ${method}()`, args);
  }
}

/* ----------------------------------------------------------------------------
 * Mock-реализация
 * -------------------------------------------------------------------------- */

/**
 * Режим mock-`requestContact` для тестирования всех 3 веток UX-спеки 5.4
 * без подмены реального MAX-бриджа. Управляется из DevToolbar.
 *  - 'success' — родитель поделился номером, возвращается mock-номер.
 *  - 'cancel' — родитель нажал «Отмена» в нативном диалоге.
 *  - 'unavailable' — старая версия MAX / bridge технически не поддерживает
 *    requestContact (в коде это симулируется reject-ом со специальным error.code).
 */
export type MockRequestContactMode = 'success' | 'cancel' | 'unavailable';

let mockRequestContactMode: MockRequestContactMode = 'success';

export function setMockRequestContactMode(mode: MockRequestContactMode): void {
  mockRequestContactMode = mode;
  console.log(`${TAG} mock requestContact mode → ${mode}`);
}

export function getMockRequestContactMode(): MockRequestContactMode {
  return mockRequestContactMode;
}

/** Sentinel-ошибка: requestContact недоступен (не поддерживается bridge-ом). */
export class RequestContactUnavailableError extends Error {
  constructor() {
    super('requestContact_unavailable');
    this.name = 'RequestContactUnavailableError';
  }
}

function createMockInitData(): MaxInitData {
  return {
    query_id: 'mock-query-id',
    auth_date: Math.floor(Date.now() / 1000),
    hash: 'mock-hash',
    user: {
      id: 12345,
      first_name: 'Анна',
      last_name: 'Тестовая',
      username: 'anna_test',
      language_code: 'ru',
      photo_url: '',
    },
    chat: {
      id: 67890,
      type: 'DIALOG',
    },
    start_param: '',
  };
}

function detectColorScheme(): MaxColorScheme {
  // Phase 5: full dark theme support. Mock bridge читает системную тему
  // через matchMedia — real bridge заполняет colorScheme из window.WebApp
  // напрямую (MAX SDK). ThemeContext consume'ит через lib/theme.ts:
  // getInitialTheme() и переключает <html data-theme=...> tokens.
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function createMockBridge(): MaxWebApp {
  const initDataUnsafe = createMockInitData();
  const initData = encodeURIComponent(JSON.stringify(initDataUnsafe));

  const listeners = new Map<string, Set<() => void>>();

  const bridge: MaxWebApp = {
    initData,
    initDataUnsafe,
    platform: 'web' as MaxPlatform,
    version: 'mock-1.0',
    colorScheme: detectColorScheme(),

    ready: () => logCall('ready'),
    close: () => logCall('close'),

    requestContact: async () => {
      logCall('requestContact', { mode: mockRequestContactMode });
      // Имитируем небольшую задержку нативного диалога.
      await new Promise((r) => setTimeout(r, 200));
      switch (mockRequestContactMode) {
        case 'cancel':
          return Promise.reject(new Error('user_declined'));
        case 'unavailable':
          return Promise.reject(new RequestContactUnavailableError());
        case 'success':
        default: {
          const result: MaxRequestContactResult = { phone: '+79991234567' };
          return result;
        }
      }
    },

    enableClosingConfirmation: () => logCall('enableClosingConfirmation'),
    disableClosingConfirmation: () => logCall('disableClosingConfirmation'),

    openLink: (url: string) => {
      logCall('openLink', url);
      if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    openMaxLink: (url: string) => {
      logCall('openMaxLink', url);
      if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },

    onEvent: (event: string, callback: () => void) => {
      logCall('onEvent', event);
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event)!.add(callback);
    },
    offEvent: (event: string, callback: () => void) => {
      logCall('offEvent', event);
      listeners.get(event)?.delete(callback);
    },
  };

  return bridge;
}

/* ----------------------------------------------------------------------------
 * Выбор реализации
 * -------------------------------------------------------------------------- */

let bridgeInstance: MaxWebApp | null = null;

export function initMaxBridge(): MaxWebApp {
  if (bridgeInstance) return bridgeInstance;

  const mode = import.meta.env.VITE_MAX_MODE ?? 'mock';

  if (mode === 'real' && typeof window !== 'undefined' && window.WebApp) {
    bridgeInstance = window.WebApp;
    console.log(`${TAG} Real bridge initialized`, {
      platform: bridgeInstance.platform,
      version: bridgeInstance.version,
      colorScheme: bridgeInstance.colorScheme,
    });
    return bridgeInstance;
  }

  // Fallback: mock — для локальной разработки или если window.WebApp ещё не доступен
  bridgeInstance = createMockBridge();
  if (mode === 'real') {
    console.warn(
      `${TAG} VITE_MAX_MODE=real, но window.WebApp не найден — используется mock. ` +
        `Это нормально вне MAX, но в продакшене стоит проверить инжекцию SDK.`,
    );
  } else {
    console.log(`${TAG} Mock initialized`, {
      user: bridgeInstance.initDataUnsafe.user,
      colorScheme: bridgeInstance.colorScheme,
    });
  }
  return bridgeInstance;
}

export function getMaxBridge(): MaxWebApp {
  if (!bridgeInstance) {
    return initMaxBridge();
  }
  return bridgeInstance;
}
